import { UserPen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { InfoTooltip } from '~/components/common/info-tooltip/info-tooltip';

import type { OperatorEditsSummary } from '../types/run-detail.api.types';
import { isExcludedCountUnknown } from '../utils/operator-edits';
import { formatCount } from '../utils/run-format';

import { Callout } from './callout';
import { HintTerm } from './hint-term';

interface Props {
  /** `delivery.operatorEdits` — null/undefined (not reported, or an older backend) renders nothing. */
  edits: OperatorEditsSummary | null | undefined;
  /** `delivery.attempt` — a retry with no `RUN` scope reported shows excluded as "—". */
  deliveryAttempt?: number | null | undefined;
}

/**
 * A11 — what integrations reported doing with the operator's edits on this delivery: one line
 * "N edits · M reset · K excluded delivered" (with an undercount note when an attempt never
 * reported), or an honest warning when the push went out WITHOUT the overlay (`unavailable`).
 * A narrowed / retried push shows excluded as "—" with a tooltip: its count is not the run's.
 */
export function OperatorEditsDeliveryNote({ edits, deliveryAttempt }: Props) {
  const { t } = useTranslation('catalog-enrichment-content-gen');
  if (!edits) return null;

  if (edits.unavailable) {
    const reason = edits.unavailableReason?.trim();
    return (
      <Callout
        tone="warning"
        title={
          <span className="inline-flex items-center gap-1.5">
            {t('overrides.delivery.unavailableTitle')}
            {reason ? (
              <InfoTooltip
                content={t('overrides.delivery.unavailableReason', { reason })}
                delayDuration={200}
              />
            ) : null}
          </span>
        }
      >
        {t('overrides.delivery.unavailableBody')}
      </Callout>
    );
  }

  const excludedUnknown = isExcludedCountUnknown(edits, deliveryAttempt);
  return (
    <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
      <UserPen aria-hidden="true" className="text-info mt-px size-3.5 shrink-0" />
      <span>
        <HintTerm
          hint={t('overrides.delivery.summaryHint')}
          className="text-foreground font-medium"
        >
          {t('overrides.delivery.summary', {
            edits: t('overrides.delivery.edits', {
              count: edits.applied,
              formatted: formatCount(edits.applied),
            }),
            reset: formatCount(edits.reset),
            excluded: excludedUnknown ? '—' : formatCount(edits.excluded),
          })}
        </HintTerm>
        {excludedUnknown ? (
          <>
            {' '}
            <InfoTooltip content={t('overrides.delivery.excludedUnknown')} delayDuration={200} />
          </>
        ) : null}
        {edits.complete ? null : ` · ${t('overrides.delivery.incomplete')}`}
      </span>
    </p>
  );
}
